import {map, shareReplay, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import {Observable, Subject, BehaviorSubject, combineLatest} from 'rxjs';
import {environment} from '../../../environments/environment';
import {InfoFichaDashboard, Periodo} from '../../_models/interfaces';
import {Meta, Criterio} from '../../_models/meta';
import {Unidad} from '../../_models/unidad';
import {Evento} from '../../_models/evento';
import {EventoUnidad} from '../../_models/evento-unidad';
import { Curso } from 'src/app/_models/curso';

const API_URL = environment.apiUrl;
const urlsApi = {
  dashboard: 'api/Dashboard',
  periodos: 'api/Periodos',
  cursos: 'api/Cursos',
  mapaCurricular: 'api/Metas/Buscar',
  mapaCurricularUnidades: 'api/Unidades/Buscar',
  materiasPeriodo: 'api/MateriasPeriodo',
  areas: 'api/Areas',
  categorias: 'api/Categorias',
  personas: 'api/Personas',
  metas: {
    crear: 'api/Metas/crear',
    editar: 'api/Metas/actualizar'
  },
  unidades: {
    get: 'api/Unidades',
    crear: 'api/Unidades/crear',
    editar: 'api/Unidades/actualizar',
    actualizarEvento: 'api/Unidades/evento/actualizar'
  },
  eventos: {
    crear: 'api/EventosAprendizaje/crear',
    editar: 'api/EventosAprendizaje/actualizar'
  },
};

@Injectable({
  providedIn: 'root'
})
export class EstudioService {

  private subject = new Subject<any>();

  private resumenUrl: string;
  private periodosUrl: string;
  private cursosUrl: string;
  private mapaCurricularUrl: string;
  private mapaCurricularUnidadesUrl: string;
  private materiasPerido: string;
  private areas: string;
  private categorias: string;
  private personasUrl: string;
  private metasUrls: any = {};
  private unidadesUrls: any = {};
  private eventosUrls: any = {};

  // Cashed observables
  areas$ = this.http.get<any[]>(`${API_URL}/api/Areas`).pipe(shareReplay(1));
  materiasPeriodo$ = this.http.get<any[]>(`${API_URL}/api/MateriasPeriodo`).pipe(shareReplay(1));
  materiasPeriodoMe$ = this.http.get<any[]>(`${API_URL}/api/MateriasPeriodo/me`).pipe(shareReplay(1));
  profesores$ = this.http.get<HttpResponse<any[]>>(`${API_URL}/api/Personas/profesor/0`).pipe(shareReplay(1));
  cursos$ = this.http.get(`${API_URL}/api/Cursos/false`).pipe(shareReplay(1));
  cursosGroup$ = this.http.get<Curso[]>(`${API_URL}/api/Cursos/true`).pipe(shareReplay(1));
  periodos$ = this.http.get<Periodo[]>(`${API_URL}/api/Periodos/false`).pipe(shareReplay(1));
  periodosYear$ = this.http.get<Periodo[]>(`${API_URL}/api/Periodos/true`).pipe(shareReplay(1));

  private cambiosEventoSubject = new BehaviorSubject({alumnoId: null, criterioId: null, unidadEventoMateriaId: null, accion: null});
  cambiosEvento$ = this.cambiosEventoSubject.asObservable();


  constructor(private http: HttpClient) {
    this.resumenUrl = urlsApi.dashboard;
    this.periodosUrl = urlsApi.periodos;
    this.cursosUrl = urlsApi.cursos;
    this.mapaCurricularUrl = urlsApi.mapaCurricular;
    this.mapaCurricularUnidadesUrl = urlsApi.mapaCurricularUnidades;
    this.materiasPerido = urlsApi.materiasPeriodo;
    this.areas = urlsApi.areas;
    this.categorias = urlsApi.categorias;
    this.personasUrl = urlsApi.personas;
    this.metasUrls.crear = urlsApi.metas.crear;
    this.metasUrls.editar = urlsApi.metas.editar;
    this.unidadesUrls.get = urlsApi.unidades.get;
    this.unidadesUrls.crear = urlsApi.unidades.crear;
    this.unidadesUrls.editar = urlsApi.unidades.editar;
    this.unidadesUrls.actualizarEvento = urlsApi.unidades.actualizarEvento;
    this.eventosUrls.crear = urlsApi.eventos.crear;
    this.eventosUrls.editar = urlsApi.eventos.editar;

  }

  getDashboard(id: number = 0, idPeriodo: number)/*: Observable<HttpResponse<InfoFichaDashboard[]>>*/ {
    const requestOptions = {
        params: new HttpParams()
      },
      url = `${API_URL}/${this.resumenUrl}/${id}/${idPeriodo}`;

    return this.http.get<HttpResponse<InfoFichaDashboard[]>>(url, requestOptions).pipe(map(info => {
      return info;
    }));
  }


  setUnidadFavorita(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/unidades/favorita`, modelo, {});
  }

  getPeriodos(year: boolean = false): Observable<Periodo[]> {
    if (year) {
      return this.periodosYear$;
    } else {
      return this.periodos$;
    }
  }

  getPeriodoActual(parent: boolean = true): Observable<Periodo> {
    const params = new HttpParams().set('isParent', parent.toString());
    return this.http.get<Periodo>(`${API_URL}/api/periodos/actual`, {params});
  }

  getCurso(id) {
    return this.http.get(`${API_URL}/api/Cursos/curso/${id}`);
  }

  getCursos(group: boolean = false) {
    if (group) {
      return this.cursosGroup$;
    } else {
      return this.cursos$;
    }
  }

  getMateriasPeriodo() {
    return this.materiasPeriodo$;
  }

  getMateriasByPeriodo(periodoId: number) {
    return this.http.get(`${API_URL}/${this.materiasPerido}/periodo/${periodoId}`);
  }

  getMateriasPeriodoMe() {
    return this.materiasPeriodoMe$;
  }

  getAreas() {
    return this.areas$;
  }

  getMapaCurricular(idsMetas: string, idsCursos: string, getUnidades: number)/*: Observable<HttpResponse<Meta[]>>*/ {
    return this.http.get<HttpResponse<Meta[]>>(`${API_URL}/${this.mapaCurricularUrl}/${idsMetas}/${idsCursos}/1`);
  }

  getMapaCurricularUnidades(idsMetas, idsCursos) {
    return this.http.get<HttpResponse<Unidad[]>>(`${API_URL}/${this.mapaCurricularUnidadesUrl}/${idsMetas}/${idsCursos}`);
  }

  getCategorias(tipo: string, group: boolean): Observable<HttpResponse<any[]>> {
    return this.http.get<HttpResponse<any[]>>(`${API_URL}/${this.categorias}/${tipo}/${group}`);
  }

  getCategoriasByAgrupador(ids: string): Observable<HttpResponse<any[]>> {
    return this.http.get<HttpResponse<any[]>>(`${API_URL}/${this.categorias}/agrupador/${ids}`);

  }

  getProfesores(periodoID = 0) {
    if (periodoID === 0) {
      return this.profesores$;
    } else {
      return this.http.get<HttpResponse<any[]>>(`${API_URL}/${this.personasUrl}/profesor/${periodoID}`);
    }
  }

  getAlumnosIEP(idsMCP) {
    return this.http.get(`${API_URL}/${this.personasUrl}/alumnoiep/mcp/${idsMCP}`);
  }

  crearMeta(meta: Meta): Observable<any> {
    return this.http.post<any>(`${API_URL}/${this.metasUrls.crear}`, meta, {});
  }

  actualizarMeta(meta: Meta): Observable<any> {
    return this.http.put<any>(`${API_URL}/${this.metasUrls.editar}`, meta, {});
  }

  /*Criterios*/
  actualizarCriterio(criterio: Criterio) {
    return this.http.put<any>(`${API_URL}/api/Criterios/actualizar`, criterio, {});
  }

  agregarCriterio(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/Criterios/crear`, modelo, {});
  }

  agregarNivelCriterio(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/Criterios/Nivel/crear`, modelo, {});
  }

  eliminarNivelCriterio(criterioID, nivel = 0) {
    return this.http.delete<HttpResponse<any>>(`${API_URL}/api/Criterios/borrar/${criterioID}/${nivel}`, {});
  }

  estadoEventoMeta(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/Unidades/evento/meta/conectar`, modelo, {});
  }

  getMeta(id, criterios = false, versionId = 0) {
    return this.http.get<HttpResponse<any>>(`${API_URL}/api/metas/${id}${versionId !== 0 ? '/version/' + versionId : ''}${criterios ? '/criterios' : ''}`, {});
  }

  getUnidad(id: string | number, etapas?: boolean): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/${id}${etapas ? '/etapas' : ''}`, {});
  }

  getUnidadMetas(id: string | number, alumnoID) {
    return this.http.get(`${API_URL}/api/Unidades/${id}/alumno/${alumnoID}`, {});
  }

  crearUnidad(unidad: Unidad): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.crear}`, unidad, {});
  }

  actualizarUnidad(unidad: Unidad): Observable<HttpResponse<any>> {
    return this.http.put<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.editar}`, unidad, {});
  }

  getMetasImport(idUnidad) {
    return this.http.get<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/meta/importar/${idUnidad}`, {});
  }

  importarMetas(objeto) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/meta/importar`, objeto, {});
  }

  duplicarMetaUnidad(idMeta, idCat) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/meta/duplicar`, {
      idOrigen: idMeta,
      idDestino: idCat
    });
  }

  getEventosImport(idUnidad) {
    return this.http.get<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/evento/importar/${idUnidad}`, {});
  }

  importarEventos(objeto) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/evento/importar`, objeto, {});
  }

  duplicarEventoUnidad(eventoUnidadID, idCategoria) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.get}/evento/duplicar`, {
      idOrigen: eventoUnidadID,
      idDestino: idCategoria
    });
  }

  crearEvento(evento: Evento): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(`${API_URL}/${this.eventosUrls.crear}`, evento, {});
  }

  actualizarEvento(evento: EventoUnidad): Observable<HttpResponse<any>> {
    return this.http.put<HttpResponse<any>>(`${API_URL}/${this.unidadesUrls.actualizarEvento}`, evento, {});
  }

  actualizarElemento(tipo, objeto, contexto?) {
    if (tipo === 'meta') {
      const meta = new Meta(objeto);
      this.subject.next({tipo: 'actualizar-meta', meta});
    }
    if (tipo === 'unidad') {
      const unidad = new Unidad(objeto);
      this.subject.next({tipo: 'actualizar-unidad', unidad});
    }
    if (tipo === 'nueva-unidad') {
      const unidad = new Unidad(objeto);
      this.subject.next({tipo: 'nueva-unidad', unidad});
    }
    if (tipo === 'evento') {
      const eventoUnidad = new EventoUnidad(objeto);
      this.subject.next({tipo: 'actualizar-evento', eventoUnidad});
    }
  }

  actualizarListaEventos(eventoUnidad) {
    this.subject.next({tipo: 'actualizar-lista-eventos', eventoUnidad});
  }

  actualizarLista(objeto, contexto, continuar?) {
    this.subject.next({tipo: 'actualizar-lista', objeto, contexto, continuar});
  }

  agregarElemento(objeto) {
    this.subject.next({tipo: 'agregar-elemento', objeto});
  }

  getEstudioServiceObservable() {
    return this.subject.asObservable();
  }

  getNivelacionesMCP(id: string | number, idMes: string | number) {
    return this.http.get<HttpResponse<any>>(`${API_URL}/api/Nivelaciones/materia/${id}/${idMes}`, {});
  }

  getNivelacionesAlumno(idAlumno: string | number, idMes: string | number) {
    return this.http.get<HttpResponse<any>>(`${API_URL}/api/Nivelaciones/alumno/${idAlumno}/mes/${idMes}`, {});
  }

  setEstadoCitacionNivelacion(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/Nivelaciones/citar`, modelo, {});
  }

  eliminarNivelacionEspecial(modelo) {
    return this.http.post<HttpResponse<any>>(`${API_URL}/api/Nivelaciones/especial/remover`, modelo, {});
  }

  setEstadoAsistenciaNivelacion(modelo) {
    return this.http.put<HttpResponse<any>>(`${API_URL}/api/Nivelaciones/asistencia`, modelo, {});
  }

  // Métodos para familia
  getHijo(hijoID: number | string) {
    return this.http.get(`${API_URL}/api/personas/alumno/${hijoID}`);
  }

  getMateriasHijo(hijoID: number | string, periodoID: number | string) {
    return this.http.get(`${API_URL}/api/MateriasPeriodo/alumno/${hijoID}/periodo/${periodoID}`);
  }

  // Métodos retroalimentacion
  getEventoDataRetroalimentacion(eventoUnidadID, alumnosIDS?) {
    return this.http.get<any>(`${API_URL}/api/EventosAlumno/unidadevento/${eventoUnidadID}${alumnosIDS ? '/alumnos/' + alumnosIDS : ''}`);
  }

  getEventoDataRetroalimentacionV2(eventoUnidadID, alumnosIDS?) {
    return this.http.get<any>(`${API_URL}/api/v2/EventosAlumno/unidadevento/${eventoUnidadID}`);
  }

  alumnosRetroalimentacionEvento(id) {
    return this.http.get(`${API_URL}/api/v2/EventosAlumno/unidadevento/${id}/alumnos`);
  }

  getDashboardRetroalimentacion(materiaID, alumnoID) {
    return this.http.get(`${API_URL}/api/EventosAlumno/materia/${materiaID}${alumnoID > 0 ? '/alumno/' + alumnoID : ''}`);
  }

  getMetaVersion(id: number, versionId: number) {
    return this.http.get<Meta>(`${API_URL}/api/Metas/${id}/version/${versionId}`);
  }

  metasPendientes(unidadId: number): Observable<Meta[]> {
    return this.http.get<Meta[]>(`${API_URL}/api/Unidades/meta/importar/${unidadId}`);
  }

  actualizarVersionMetaUnidad(unidadId: number, metaUnidadId: number, versionId: number): Observable<any> {
    const model = {
      unidadId: unidadId,
      metaId: metaUnidadId,
      versionId: versionId
    };
    this.subject.next({tipo: 'actualizar-version', model});
    return this.http.put<any>(`${API_URL}/api/Unidades/meta/version`, model);
  }

  cambiosEmit(alumnoId: number, criterioId: number, unidadEventoMateriaId: number, accion: string) {
    this.cambiosEventoSubject.next(
      {alumnoId, criterioId, unidadEventoMateriaId , accion: accion});
  }

  public extractData(res: any) {
    const body = res.hasOwnProperty('items') ? res.items : res;
    return body || {};
  }
}
